import { EnvironmentProviders, inject, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoggerService } from '@telespot/shared/logger/feature/util';
import { RecorderService } from '@telespot/shared/session-recorder';
import { AuthService } from '@telespot/web-core';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';

/**
 * Attaches the signed-in user to the session recording while consent stands. Lives in the app
 * because it joins two modules the recorder itself must not depend on.
 */
export function provideRecorderIdentity(): EnvironmentProviders {
  return makeEnvironmentProviders([provideAppInitializer(() => identifyRecordedSessions())]);
}

/**
 * Consent can be granted after sign-in — the dialog is re-openable from the footer — so both
 * sources are watched instead of consent being sampled once when the user arrives.
 *
 * Must run inside an injection context.
 */
export function identifyRecordedSessions(): void {
  const auth = inject(AuthService);
  const recorder = inject(RecorderService);
  const logger = inject(LoggerService, { optional: true });

  combineLatest([auth.currentUser$, recorder.consent$])
    .pipe(
      filter(([user, consented]) => consented && !!user),
      distinctUntilChanged(([previous], [current]) => previous === current),
      takeUntilDestroyed()
    )
    .subscribe(([user]) => {
      const organization = user.affiliations?.[0]?.organization?.name;

      recorder.identify(organization ? `${organization}_${user.username}` : user.username, {
        displayName: user.username,
        email: user.email,
      });
      logger?.debug(`Recorder identified ${user.username}`);
    });
}
